import { computed, Injectable, signal } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { SupplierOrdersRepository } from '../supplier-orders.repository'
import {
  SupplierOrdersListingData,
  SupplierOrdersListingPage,
} from '../models/supplier-order-listing.model'
import { SupplierOrder } from '../models/supplier-order.entity'
import {
  SupplierOrderField,
  SupplierOrderSearchParams,
} from '../models/supplier-order-search.model'
import { SupplierOrdersService } from '../supplier-orders.service'
import { Observable } from 'rxjs'
import { WarehousesService } from '../../warehouses'
import { SuppliersService } from '../../suppliers'
import { mergeById } from '../../../libs/filter.lib'

interface SupplierOrdersListingState {
  filters?: SupplierOrderSearchParams
  data?: SupplierOrder[]
  totalCount?: number
  error?: HttpErrorResponse
  isLoading: boolean
  isInitialized: boolean
  extData?: SupplierOrdersListingData
  dropdownOptions?: SupplierOrdersListingData
  searchString?: string
  searchKeys?: SupplierOrderSearchParams
  customFilters?: SupplierOrderSearchParams
  fields?: SupplierOrderField[]
}

const SUPPLIER_ORDERS_INITIAL_STATE: SupplierOrdersListingState = {
  isLoading: false,
  isInitialized: false,
}

@Injectable()
export class SupplierOrdersListingStore {
  constructor(
    private supplierOrdersService: SupplierOrdersService,
    private supplierOrdersRepository: SupplierOrdersRepository,
    private suppliersService: SuppliersService,
    private warehousesService: WarehousesService,
  ) {}

  private state = signal<SupplierOrdersListingState>(
    SUPPLIER_ORDERS_INITIAL_STATE,
  )

  // Selectors

  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly isFiltered = computed(() => {
    const filters = this.state().filters
    return !!filters && Object.keys(filters).length > 0
  })
  readonly isErrored = computed(() => !!this.state().error)
  readonly supplierOrders = computed(() => this.state().data || [])
  readonly extData = computed(() => this.state().extData)
  readonly dropdownOptions = computed(() => this.state().dropdownOptions)
  readonly totalCount = computed(() => this.state().totalCount || 0)
  readonly searchKeys = computed(() => this.state().searchKeys)
  readonly fields = computed(() => this.state().fields)

  // Effects

  async search(
    params?: SupplierOrderSearchParams,
    returnAll?: boolean,
  ): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.supplierOrdersRepository
        .searchSupplierOrders$(
          this.parseStateFilterParams(this.state(), params),
          this.state().fields ?? [],
          this.state().extData,
          returnAll,
        )
        .subscribe({
          next: (data) => {
            resolve()
            this.setData(data)
          },
          error: (error) => {
            reject(error)
            this.setError(error)
          },
        })
    })
  }

  async delete(supplierOrder: SupplierOrder): Promise<boolean> {
    if (supplierOrder.attachments?.length) {
      this.supplierOrdersRepository.notify({ code: 'ATTACHMENTS_FOUND' })
      return false
    }

    return new Promise<boolean>((resolve) => {
      this.supplierOrdersRepository
        .alert('SUPPLIER_ORDER_DELETION', supplierOrder)
        .then((response) => {
          if (response === 'success') {
            this.setIsLoading(true)
            this.supplierOrdersService.delete$(supplierOrder._id).subscribe({
              next: () => {
                resolve(true)
                this.supplierOrdersRepository.notify({
                  code: 'SUPPLIER_ORDER_DELETED',
                })
              },
              error: (error: HttpErrorResponse) => {
                resolve(false)
                this.setError(error)
              },
            })
          }
        })
    })
  }

  async searchLookup(event: {
    dataSourceKey: string
    q?: string
    dataKeyField?: string
    dataDisplayField?: string
    offset?: number
    limit?: number
  }): Promise<void> {
    type LookupKey = keyof SupplierOrdersListingData
    type ArrayElement<T> = T extends (infer U)[] ? U : never
    type LookupValue = ArrayElement<
      NonNullable<SupplierOrdersListingData[LookupKey]>
    >

    const queryKey = (event.dataDisplayField ?? 'name') + ':ct'
    const offset = event.offset ?? 0
    const limit = event.limit ?? 50
    const query = {
      offset,
      limit,
      ...(event.q ? { [queryKey]: event.q } : {}),
    } as Record<string, unknown>

    const serviceMap: Partial<
      Record<LookupKey, () => Observable<LookupValue[]>>
    > = {
      warehouses: () => this.warehousesService.list$(query),
      suppliers: () => this.suppliersService.list$(query),
    }

    const search$ = serviceMap[event.dataSourceKey as LookupKey]
    if (!search$) return

    search$().subscribe((items) => {
      this.state.update((s) => {
        const key = event.dataSourceKey as LookupKey
        const prev = (s.dropdownOptions?.[key] ?? []) as { _id: string }[]
        const incoming = items as { _id: string }[]
        const merged = prev.length > 0 ? mergeById(prev, incoming) : incoming
        return {
          ...s,
          dropdownOptions: {
            ...s.extData,
            [event.dataSourceKey]: merged as LookupValue[],
          },
        }
      })
    })
  }

  /**
   * REDUCERS
   */

  setData(data: SupplierOrdersListingPage) {
    const extData = this.extData()
    const newExtData = {
      warehouses: mergeById(extData?.warehouses, data.extData?.warehouses),
      suppliers: mergeById(extData?.suppliers, data.extData?.suppliers),
    }
    this.state.set({
      ...this.state(),
      data: data.data,
      totalCount: data.totalCount,
      extData: newExtData,
      dropdownOptions: newExtData,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  setTotalCount(count: number) {
    this.state.set({
      ...this.state(),
      totalCount: count,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setFilters(filters?: SupplierOrderSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...filters },
    })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  addFilter(filter: SupplierOrderSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...this.state().filters, ...filter },
    })
  }

  removeFilter(filter: SupplierOrderSearchParams) {
    const currentFilters = this.state().filters
    if (currentFilters) {
      // Create a new filters object without the specified filter properties
      const updatedFilters = { ...currentFilters }

      // Remove each property specified in the filter parameter
      for (const key of Object.keys(filter)) {
        if (key in updatedFilters) {
          delete updatedFilters[key as keyof SupplierOrderSearchParams]
        }
      }

      this.state.set({
        ...this.state(),
        isInitialized: true,
        filters: updatedFilters,
      })
    }
  }

  setFields(fields?: SupplierOrderField[]) {
    this.state.set({ ...this.state(), fields })
  }

  setSearchKeys(searchKeys?: SupplierOrderSearchParams) {
    this.state.set({ ...this.state(), searchKeys })
  }

  resetLookups() {
    this.state.set({
      ...this.state(),
      dropdownOptions: undefined,
      extData: undefined,
    })
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      filters: {},
    })
  }

  private parseStateFilterParams(
    state: SupplierOrdersListingState,
    filters?: SupplierOrderSearchParams,
  ): SupplierOrderSearchParams {
    let params: SupplierOrderSearchParams = filters || {}
    if (state.searchString) {
      params.q = state.searchString
    }
    if (state.customFilters) {
      params = { ...params, ...state.customFilters }
    }
    if (state.searchKeys) {
      params = { ...params, ...state.searchKeys }
    }

    return params
  }
}
